import { 
  collection, 
  query, 
  where, 
  getAggregateFromServer, 
  count, 
  doc as firestoreDoc, 
  getDoc, 
  sum, 
  average,
  getDocs, 
  orderBy, 
  limit, 
  startAfter, 
  updateDoc,
  QueryDocumentSnapshot, 
  DocumentData, 
  getCountFromServer,
  serverTimestamp,
  addDoc,
  setDoc,
  Query,
  CollectionReference
} from 'firebase/firestore';
import { db } from './firebase';
import { 
  AffiliateApplication, 
  AffiliateWithStats,
  CommissionRecord,
  PayoutRecord,
  PayoutSchedule,
  ClickRecord,
  ConversionRecord,
  AffiliateStats
} from '../types';
import { getCurrentCommissionRate } from './commissionSettingsService';

// Pagination result type
export interface PaginatedAffiliatesResult {
  affiliates: AffiliateWithStats[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total?: number;
}

// Search result type
export interface SearchAffiliatesResult {
  affiliates: AffiliateWithStats[];
  total: number;
}

/**
 * Helper function to process affiliate application data consistently
 */
const processAffiliateData = async (docSnapshot: QueryDocumentSnapshot<DocumentData>): Promise<AffiliateWithStats> => {
  const data = docSnapshot.data();
  
  // Helper function to convert Firestore timestamp to Date
  const convertTimestamp = (timestamp: any): Date => {
    if (!timestamp) {
      return new Date();
    }
    
    if (timestamp && typeof timestamp === 'object' && timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? new Date() : date;
    }
    
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    
    return new Date();
  };

  // Get user data - prefer stored data in application, fallback to users collection
  let userName = data.userName || '';
  let userEmail = data.userEmail || '';
  
  // If no stored user data, fetch from users collection (for backward compatibility)
  if (!userName || !userEmail) {
    try {
      const userDocRef = firestoreDoc(db, 'users', data.userId);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data() as {
          name?: string;
          displayName?: string;
          email?: string;
          [key: string]: any;
        };
        if (!userName) {
          userName = userData.name || userData.displayName || userData.email?.split('@')[0] || 'Unknown';
        }
        if (!userEmail) {
          userEmail = userData.email || '';
        }
      }
    } catch (error) {
      console.error('Error fetching user data for affiliate:', error);
      // Use fallback values if fetch fails
      if (!userName) userName = 'Unknown';
      if (!userEmail) userEmail = '';
    }
  }

  // Calculate stats
  const stats = await getAffiliateStats(docSnapshot.id);

  const affiliate: AffiliateWithStats = {
    id: docSnapshot.id,
    userId: data.userId,
    userName,
    userEmail,
    profiles: data.profiles || [],
    website: data.website,
    paymentInfo: data.paymentInfo,
    status: data.status,
    referralCode: data.referralCode,
    submittedAt: convertTimestamp(data.submittedAt),
    reviewedBy: data.reviewedBy,
    reviewedAt: data.reviewedAt ? convertTimestamp(data.reviewedAt) : undefined,
    stats,
    lastActivity: data.lastActivity ? convertTimestamp(data.lastActivity) : undefined
  };

  return affiliate;
};

/**
 * Get affiliate clicks within date range
 */
export const getAffiliateClicksInDateRange = async (
  affiliateId: string,
  startDate?: Date,
  endDate?: Date
): Promise<number> => {
  try {
    let q = query(
      collection(db, 'clickRecords'),
      where('affiliateId', '==', affiliateId)
    );

    // Add date range filter if provided
    if (startDate && endDate) {
      q = query(q, 
        where('timestamp', '>=', startDate),
        where('timestamp', '<=', endDate)
      );
    }

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting affiliate clicks in date range:', error);
    return 0;
  }
};

/**
 * Get affiliate conversions within date range
 */
export const getAffiliateConversionsInDateRange = async (
  affiliateId: string,
  startDate?: Date,
  endDate?: Date
): Promise<number> => {
  try {
    let q = query(
      collection(db, 'conversionRecords'),
      where('affiliateId', '==', affiliateId)
    );

    // Add date range filter if provided
    if (startDate && endDate) {
      q = query(q, 
        where('timestamp', '>=', startDate),
        where('timestamp', '<=', endDate)
      );
    }

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting affiliate conversions in date range:', error);
    return 0;
  }
};

/**
 * Get affiliate stats (clicks, conversions, earnings) - optimized with aggregation
 */
const getAffiliateStats = async (affiliateId: string): Promise<AffiliateStats> => {
  try {
    // Run all queries in parallel for better performance
    const [clicksSnapshot, conversionsSnapshot, commissionsSnapshot] = await Promise.all([
      // Get click count
      getCountFromServer(
        query(
          collection(db, 'clickRecords'),
          where('affiliateId', '==', affiliateId)
        )
      ),
      // Get conversion count
      getCountFromServer(
        query(
          collection(db, 'conversionRecords'),
          where('affiliateId', '==', affiliateId)
        )
      ),
      // Get earnings using aggregation
      getAggregateFromServer(
        query(
          collection(db, 'commissionRecords'),
          where('affiliateId', '==', affiliateId)
        ),
        {
          totalEarnings: sum('commissionAmount'),
          totalOrderValue: sum('orderValue'),
          commissionCount: count()
        }
      )
    ]);

    const totalClicks = clicksSnapshot.data().count;
    const totalConversions = conversionsSnapshot.data().count;
    const commissionsData = commissionsSnapshot.data();
    const totalEarnings = commissionsData.totalEarnings || 0;
    const totalOrderValue = commissionsData.totalOrderValue || 0;
    const commissionCount = commissionsData.commissionCount || 0;

    // Calculate conversion rate and average order value
    const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
    const avgOrderValue = commissionCount > 0 ? totalOrderValue / commissionCount : 0;

    return {
      totalClicks,
      totalConversions,
      totalEarnings,
      conversionRate,
      avgOrderValue
    };
  } catch (error) {
    console.error('Error getting affiliate stats:', error);
    return {
      totalClicks: 0,
      totalConversions: 0,
      totalEarnings: 0,
      conversionRate: 0,
      avgOrderValue: 0
    };
  }
};

/**
 * Fetch affiliate applications with pagination
 */
export const fetchAffiliatesPaginated = async (
  pageSize: number = 20,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  statusFilter?: 'pending' | 'approved' | 'rejected'
): Promise<PaginatedAffiliatesResult> => {
  try {
    let q = query(
      collection(db, 'affiliateApplications'),
      orderBy('submittedAt', 'desc'),
      limit(pageSize)
    );

    // Add status filter if provided
    if (statusFilter) {
      q = query(
        collection(db, 'affiliateApplications'),
        where('status', '==', statusFilter),
        orderBy('submittedAt', 'desc'),
        limit(pageSize)
      );
    }

    // Add pagination if lastDoc provided
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    
    // Process documents in parallel
    const affiliates = await Promise.all(
      querySnapshot.docs.map(docSnapshot => processAffiliateData(docSnapshot))
    );

    // Get total count
    let totalQuery: CollectionReference<DocumentData> | Query<DocumentData> = collection(db, 'affiliateApplications');
    if (statusFilter) {
      totalQuery = query(collection(db, 'affiliateApplications'), where('status', '==', statusFilter));
    }
    const totalSnapshot = await getCountFromServer(totalQuery);
    const total = totalSnapshot.data().count;

    return {
      affiliates,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
      hasMore: querySnapshot.docs.length === pageSize,
      total
    };
  } catch (error) {
    console.error('Error fetching affiliates:', error);
    throw new Error('Failed to fetch affiliates');
  }
};

/**
 * Search affiliates by name or email
 */
export const searchAffiliates = async (
  searchQuery: string,
  statusFilter?: 'pending' | 'approved' | 'rejected'
): Promise<SearchAffiliatesResult> => {
  try {
    // First get all applications (with status filter if provided)
    let q = query(collection(db, 'affiliateApplications'));
    if (statusFilter) {
      q = query(q, where('status', '==', statusFilter));
    }
    
    const querySnapshot = await getDocs(q);
    
    // Process all documents
    const allAffiliates = await Promise.all(
      querySnapshot.docs.map(docSnapshot => processAffiliateData(docSnapshot))
    );

    // Filter by search query on the client side
    const searchTerm = searchQuery.toLowerCase();
    const filteredAffiliates = allAffiliates.filter(affiliate => 
      affiliate.userName.toLowerCase().includes(searchTerm) ||
      affiliate.userEmail.toLowerCase().includes(searchTerm) ||
      (affiliate.referralCode && affiliate.referralCode.toLowerCase().includes(searchTerm))
    );

    return {
      affiliates: filteredAffiliates,
      total: filteredAffiliates.length
    };
  } catch (error) {
    console.error('Error searching affiliates:', error);
    throw new Error('Failed to search affiliates');
  }
};

/**
 * Approve an affiliate application
 */
export const approveAffiliateApplication = async (
  applicationId: string,
  referralCode: string,
  reviewedBy: string
): Promise<void> => {
  try {
    // Update application status
    const applicationRef = firestoreDoc(db, 'affiliateApplications', applicationId);
    await updateDoc(applicationRef, {
      status: 'approved',
      referralCode,
      reviewedBy,
      reviewedAt: serverTimestamp()
    });

    // Get application data to update user
    const applicationDoc = await getDoc(applicationRef);
    if (applicationDoc.exists()) {
      const applicationData = applicationDoc.data();
      
      // Update user's isAffiliate status
      const userRef = firestoreDoc(db, 'users', applicationData.userId);
      await updateDoc(userRef, {
        isAffiliate: true
      });

      // The email will be sent automatically via the Firestore trigger (onAffiliateStatusChange)
      // No need to manually call the email function here
    }
  } catch (error) {
    console.error('Error approving affiliate application:', error);
    throw new Error('Failed to approve affiliate application');
  }
};

/**
 * Reject an affiliate application
 */
export const rejectAffiliateApplication = async (
  applicationId: string,
  reviewedBy: string
): Promise<void> => {
  try {
    const applicationRef = firestoreDoc(db, 'affiliateApplications', applicationId);
    await updateDoc(applicationRef, {
      status: 'rejected',
      reviewedBy,
      reviewedAt: serverTimestamp()
    });

    // The email will be sent automatically via the Firestore trigger (onAffiliateStatusChange)
    // No need to manually call the email function here
  } catch (error) {
    console.error('Error rejecting affiliate application:', error);
    throw new Error('Failed to reject affiliate application');
  }
};

/**
 * Get payout schedule configuration
 */
export const getPayoutSchedule = async (): Promise<PayoutSchedule | null> => {
  try {
    const scheduleDoc = await getDoc(firestoreDoc(db, 'payoutSchedule', 'config'));
    if (!scheduleDoc.exists()) {
      return null;
    }

    const data = scheduleDoc.data();
    const commissionRate = await getCurrentCommissionRate();
    
    return {
      id: scheduleDoc.id,
      frequency: data.frequency,
      dayOfMonth: data.dayOfMonth,
      time: data.time,
      nextPayoutDate: data.nextPayoutDate?.toDate() || new Date(),
      commissionPercentage: commissionRate
    };
  } catch (error) {
    console.error('Error getting payout schedule:', error);
    throw new Error('Failed to get payout schedule');
  }
};

/**
 * Update payout schedule configuration
 */
export const updatePayoutSchedule = async (schedule: Omit<PayoutSchedule, 'id'>): Promise<void> => {
  try {
    await setDoc(firestoreDoc(db, 'payoutSchedule', 'config'), {
      ...schedule,
      nextPayoutDate: schedule.nextPayoutDate
    });
  } catch (error) {
    console.error('Error updating payout schedule:', error);
    throw new Error('Failed to update payout schedule');
  }
};

// Pagination result types for commission records and payouts
export interface PaginatedCommissionsResult {
  commissions: CommissionRecord[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total: number;
}

export interface PaginatedPayoutsResult {
  payouts: PayoutRecord[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total: number;
}

/**
 * Get paginated commission records for an affiliate with date filtering
 */
export const getAffiliateCommissionsPaginated = async (
  affiliateId: string,
  pageSize: number = 20,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  startDate?: Date,
  endDate?: Date
): Promise<PaginatedCommissionsResult> => {
  try {
    let q = query(
      collection(db, 'commissionRecords'),
      where('affiliateId', '==', affiliateId),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    // Add date filtering if provided
    if (startDate && endDate) {
      q = query(
        collection(db, 'commissionRecords'),
        where('affiliateId', '==', affiliateId),
        where('createdAt', '>=', startDate),
        where('createdAt', '<=', endDate),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    }

    // Add pagination if lastDoc provided
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    
    const commissions = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        affiliateId: data.affiliateId,
        orderId: data.orderId,
        orderValue: data.orderValue,
        commissionAmount: data.commissionAmount,
        commissionRate: data.commissionRate || 10,
        status: data.status || 'pending',
        paymentStatus: data.paymentStatus || 'pending',
        source: data.source || 'link', // Default to 'link' for backward compatibility
        discountCodeId: data.discountCodeId || undefined,
        createdAt: data.createdAt?.toDate() || new Date()
      };
    });

    // Get total count for the same filter criteria
    let countQuery = query(
      collection(db, 'commissionRecords'),
      where('affiliateId', '==', affiliateId)
    );

    if (startDate && endDate) {
      countQuery = query(
        collection(db, 'commissionRecords'),
        where('affiliateId', '==', affiliateId),
        where('createdAt', '>=', startDate),
        where('createdAt', '<=', endDate)
      );
    }

    const countSnapshot = await getCountFromServer(countQuery);
    const total = countSnapshot.data().count;

    return {
      commissions,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
      hasMore: querySnapshot.docs.length === pageSize,
      total
    };
  } catch (error) {
    console.error('Error getting paginated affiliate commissions:', error);
    throw new Error('Failed to get paginated affiliate commissions');
  }
};

/**
 * Get paginated payout records for an affiliate with date filtering
 */
export const getAffiliatePayoutsPaginated = async (
  affiliateId: string,
  pageSize: number = 20,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  startDate?: Date,
  endDate?: Date
): Promise<PaginatedPayoutsResult> => {
  try {
    let q = query(
      collection(db, 'payoutRecords'),
      where('affiliateId', '==', affiliateId),
      orderBy('paidAt', 'desc'),
      limit(pageSize)
    );

    // Add date filtering if provided
    if (startDate && endDate) {
      q = query(
        collection(db, 'payoutRecords'),
        where('affiliateId', '==', affiliateId),
        where('paidAt', '>=', startDate),
        where('paidAt', '<=', endDate),
        orderBy('paidAt', 'desc'),
        limit(pageSize)
      );
    }

    // Add pagination if lastDoc provided
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    
    const payouts = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        affiliateId: data.affiliateId,
        amount: data.amount,
        method: data.method || 'PayPal',
        transactionId: data.transactionId,
        status: data.status || 'completed',
        paidAt: data.paidAt?.toDate() || new Date()
      };
    });

    // Get total count for the same filter criteria
    let countQuery = query(
      collection(db, 'payoutRecords'),
      where('affiliateId', '==', affiliateId)
    );

    if (startDate && endDate) {
      countQuery = query(
        collection(db, 'payoutRecords'),
        where('affiliateId', '==', affiliateId),
        where('paidAt', '>=', startDate),
        where('paidAt', '<=', endDate)
      );
    }

    const countSnapshot = await getCountFromServer(countQuery);
    const total = countSnapshot.data().count;

    return {
      payouts,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
      hasMore: querySnapshot.docs.length === pageSize,
      total
    };
  } catch (error) {
    console.error('Error getting paginated affiliate payouts:', error);
    throw new Error('Failed to get paginated affiliate payouts');
  }
};

/**
 * Get commission records for an affiliate
 */
export const getAffiliateCommissions = async (affiliateId: string): Promise<CommissionRecord[]> => {
  try {
    const q = query(
      collection(db, 'commissionRecords'),
      where('affiliateId', '==', affiliateId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        affiliateId: data.affiliateId,
        orderId: data.orderId,
        orderValue: data.orderValue,
        commissionAmount: data.commissionAmount,
        commissionRate: data.commissionRate || 10, // Default to 10% if not set
        status: data.status || 'pending',
        paymentStatus: data.paymentStatus || 'pending',
        source: data.source || 'link', // Default to 'link' for backward compatibility
        discountCodeId: data.discountCodeId || undefined,
        createdAt: data.createdAt?.toDate() || new Date()
      };
    });
  } catch (error) {
    console.error('Error getting affiliate commissions:', error);
    throw new Error('Failed to get affiliate commissions');
  }
};

/**
 * Get payout records for an affiliate
 */
export const getAffiliatePayouts = async (affiliateId: string): Promise<PayoutRecord[]> => {
  try {
    const q = query(
      collection(db, 'payoutRecords'),
      where('affiliateId', '==', affiliateId),
      orderBy('paidAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        affiliateId: data.affiliateId,
        amount: data.amount,
        method: data.method || 'PayPal',
        transactionId: data.transactionId,
        status: data.status || 'completed',
        paidAt: data.paidAt?.toDate() || new Date()
      };
    });
  } catch (error) {
    console.error('Error getting affiliate payouts:', error);
    throw new Error('Failed to get affiliate payouts');
  }
};

/**
 * Trigger manual payout for an affiliate
 */
export const triggerAffiliatePayout = async (
  affiliateId: string,
  amount: number
): Promise<string> => {
  try {
    // Get previous month's pending commissions
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);

    // Get all commissions for this affiliate from the previous month
    // Don't filter by status in the query since commissions might not have a status field initially
    const commissionsQuery = query(
      collection(db, 'commissionRecords'),
      where('affiliateId', '==', affiliateId),
      where('createdAt', '>=', startDate),
      where('createdAt', '<=', endDate)
    );

    const commissionsSnapshot = await getDocs(commissionsQuery);
    
    // Use ALL commissions from previous month (regardless of status)
    const previousMonthCommissions = commissionsSnapshot.docs;

    const commissionIds = previousMonthCommissions.map(doc => doc.id);
    
    console.log(`Found ${commissionIds.length} commissions from previous month for affiliate ${affiliateId}`);
    
    if (commissionIds.length === 0) {
      throw new Error('No commissions found for the previous month');
    }

    // Create payout record with enhanced details
    const payoutRef = await addDoc(collection(db, 'payoutRecords'), {
      affiliateId,
      amount,
      method: 'manual', // Manual admin-triggered payout
      status: 'completed',
      paidAt: serverTimestamp(),
      commissionsIncluded: commissionIds,
      processedBy: 'admin',
      notes: `Payout for ${commissionIds.length} commissions from previous month`
    });

    // Update all included commission records to 'paid' status
    const updatePromises = previousMonthCommissions.map(async (doc) => {
      try {
        await updateDoc(doc.ref, {
          status: 'paid',
          paidAt: serverTimestamp(),
          payoutId: payoutRef.id
        });
        console.log(`Updated commission ${doc.id} to paid status`);
        return doc.id;
      } catch (error) {
        console.error(`Failed to update commission ${doc.id}:`, error);
        throw error;
      }
    });

    const updatedCommissionIds = await Promise.all(updatePromises);
    
    console.log(`Successfully updated ${updatedCommissionIds.length} commission records to 'paid' status`);

    console.log(`Successfully processed payout: ${payoutRef.id} for affiliate: ${affiliateId}, amount: ${amount}, commissions updated: ${commissionIds.length}`);
    
    return payoutRef.id;
  } catch (error) {
    console.error('Error triggering affiliate payout:', error);
    throw new Error('Failed to trigger affiliate payout');
  }
};

/**
 * Get total affiliate count using aggregation
 */
export const getTotalAffiliateCount = async (): Promise<number> => {
  try {
    console.log('Getting total affiliate count with aggregation...');
    
    const affiliatesCollection = collection(db, 'affiliateApplications');
    const snapshot = await getAggregateFromServer(
      affiliatesCollection,
      { totalCount: count() }
    );
    
    const total = snapshot.data().totalCount;
    console.log('Total affiliates (aggregation):', total);
    
    return total;
  } catch (error) {
    console.error('Error getting total affiliate count:', error);
    throw error;
  }
};

/**
 * Get pending affiliate applications count using aggregation
 */
export const getPendingApplicationCount = async (): Promise<number> => {
  try {
    console.log('Getting pending applications count with aggregation...');
    
    const affiliatesCollection = collection(db, 'affiliateApplications');
    const snapshot = await getAggregateFromServer(
      query(
        affiliatesCollection,
        where('status', '==', 'pending')
      ),
      { pendingCount: count() }
    );
    
    const pending = snapshot.data().pendingCount;
    console.log('Pending applications (aggregation):', pending);
    
    return pending;
  } catch (error) {
    console.error('Error getting pending applications count:', error);
    throw error;
  }
};

/**
 * Get approved affiliate count using aggregation
 */
export const getApprovedAffiliateCount = async (): Promise<number> => {
  try {
    console.log('Getting approved affiliates count with aggregation...');
    
    const affiliatesCollection = collection(db, 'affiliateApplications');
    const snapshot = await getAggregateFromServer(
      query(
        affiliatesCollection,
        where('status', '==', 'approved')
      ),
      { approvedCount: count() }
    );
    
    const approved = snapshot.data().approvedCount;
    console.log('Approved affiliates (aggregation):', approved);
    
    return approved;
  } catch (error) {
    console.error('Error getting approved affiliates count:', error);
    throw error;
  }
};

/**
 * Get total commissions paid using aggregation
 */
export const getTotalCommissionsPaid = async (): Promise<number> => {
  try {
    console.log('Getting total commissions paid with aggregation...');
    
    const commissionsCollection = collection(db, 'commissionRecords');
    const snapshot = await getAggregateFromServer(
      commissionsCollection,
      { totalCommissions: sum('commissionAmount') }
    );
    
    const total = snapshot.data().totalCommissions || 0;
    console.log('Total commissions paid (aggregation):', total);
    
    return total;
  } catch (error) {
    console.error('Error getting total commissions paid:', error);
    throw error;
  }
};

/**
 * Get affiliate statistics for current month (month-to-date)
 */
export const getCurrentMonthAffiliateStats = async () => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const [newAffiliatesSnapshot, commissionsSnapshot] = await Promise.all([
      // New affiliates this month
      getCountFromServer(
        query(
          collection(db, 'affiliateApplications'),
          where('submittedAt', '>=', startOfMonth),
          where('submittedAt', '<=', endOfDay)
        )
      ),
      // Commissions paid this month
      getAggregateFromServer(
        query(
          collection(db, 'commissionRecords'),
          where('createdAt', '>=', startOfMonth),
          where('createdAt', '<=', endOfDay)
        ),
        { monthlyCommissions: sum('commissionAmount') }
      )
    ]);

    return {
      newAffiliatesThisMonth: newAffiliatesSnapshot.data().count,
      monthlyCommissions: commissionsSnapshot.data().monthlyCommissions || 0
    };
  } catch (error) {
    console.error('Error getting current month affiliate stats:', error);
    return {
      newAffiliatesThisMonth: 0,
      monthlyCommissions: 0
    };
  }
};

/**
 * Get affiliate statistics for same period in previous month
 */
export const getPreviousMonthSamePeriodAffiliateStats = async () => {
  try {
    const now = new Date();
    const currentDay = now.getDate();
    
    // Calculate previous month dates
    const prevMonth = now.getMonth() - 1;
    const prevYear = prevMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const adjustedPrevMonth = prevMonth < 0 ? 11 : prevMonth;
    
    // Get the number of days in the previous month
    const daysInPrevMonth = new Date(prevYear, adjustedPrevMonth + 1, 0).getDate();
    
    // Use the minimum of current day or days available in previous month
    const comparisonDay = Math.min(currentDay, daysInPrevMonth);
    
    const prevMonthStart = new Date(prevYear, adjustedPrevMonth, 1);
    const prevMonthEnd = new Date(prevYear, adjustedPrevMonth, comparisonDay, 23, 59, 59, 999);

    const [newAffiliatesSnapshot, commissionsSnapshot] = await Promise.all([
      // New affiliates in previous month same period
      getCountFromServer(
        query(
          collection(db, 'affiliateApplications'),
          where('submittedAt', '>=', prevMonthStart),
          where('submittedAt', '<=', prevMonthEnd)
        )
      ),
      // Commissions paid in previous month same period
      getAggregateFromServer(
        query(
          collection(db, 'commissionRecords'),
          where('createdAt', '>=', prevMonthStart),
          where('createdAt', '<=', prevMonthEnd)
        ),
        { monthlyCommissions: sum('commissionAmount') }
      )
    ]);

    return {
      newAffiliatesThisMonth: newAffiliatesSnapshot.data().count,
      monthlyCommissions: commissionsSnapshot.data().monthlyCommissions || 0
    };
  } catch (error) {
    console.error('Error getting previous month same period affiliate stats:', error);
    return {
      newAffiliatesThisMonth: 0,
      monthlyCommissions: 0
    };
  }
};

/**
 * Helper function to get date range for time period
 */
const getDateRangeForPeriod = (period: 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year') => {
  const now = new Date();
  const start = new Date();
  const end = new Date();
  
  switch (period) {
    case 'today':
      // Start and end of today
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'this_week':
      // Start of this week (Sunday)
      start.setDate(now.getDate() - now.getDay());
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'this_month':
      // Start of this month
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'this_quarter':
      // Start of this quarter
      const quarterStart = Math.floor(now.getMonth() / 3) * 3;
      start.setMonth(quarterStart, 1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'this_year':
      // Start of this year
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
  }
  
  return { start, end };
};

/**
 * Get affiliate statistics for a specific time period
 */
export const getAffiliateStatisticsForPeriod = async (period: 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year') => {
  try {
    console.log(`Getting affiliate statistics for period: ${period}`);
    
    const { start, end } = getDateRangeForPeriod(period);
    
    // For time period filtering, we need to get filtered data
    const affiliatesRef = collection(db, 'affiliateApplications');
    const commissionsRef = collection(db, 'commissionRecords');
    
    // Get affiliates created within the period
    const affiliatesInPeriodQuery = query(
      affiliatesRef,
      where('submittedAt', '>=', start),
      where('submittedAt', '<=', end)
    );
    
    const pendingInPeriodQuery = query(
      affiliatesRef,
      where('submittedAt', '>=', start),
      where('submittedAt', '<=', end),
      where('status', '==', 'pending')
    );
    
    const approvedInPeriodQuery = query(
      affiliatesRef,
      where('submittedAt', '>=', start),
      where('submittedAt', '<=', end),
      where('status', '==', 'approved')
    );
    
    // Get commissions within the period
    const commissionsInPeriodQuery = query(
      commissionsRef,
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    );
    
    // Run all queries in parallel
    const [
      totalAffiliatesAll,
      pendingApplicationsAll,
      approvedAffiliatesAll,
      totalCommissionsAll,
      affiliatesInPeriodSnapshot,
      pendingInPeriodSnapshot,
      approvedInPeriodSnapshot,
      commissionsInPeriodSnapshot
    ] = await Promise.all([
      getTotalAffiliateCount(),
      getPendingApplicationCount(),
      getApprovedAffiliateCount(),
      getTotalCommissionsPaid(),
      getDocs(affiliatesInPeriodQuery),
      getDocs(pendingInPeriodQuery),
      getDocs(approvedInPeriodQuery),
      getDocs(commissionsInPeriodQuery)
    ]);
    
    // Calculate commissions for the period
    let periodCommissions = 0;
    commissionsInPeriodSnapshot.forEach(doc => {
      const data = doc.data();
      periodCommissions += data.commissionAmount || 0;
    });
    
    const current = {
      totalAffiliates: totalAffiliatesAll,
      pendingApplications: pendingApplicationsAll,
      approvedAffiliates: approvedAffiliatesAll,
      totalCommissionsPaid: periodCommissions, // Use period-filtered commissions instead of all-time
      newAffiliatesThisMonth: affiliatesInPeriodSnapshot.size,
      monthlyCommissions: periodCommissions
    };
    
    // For comparison, get stats from previous equivalent period
    const previousPeriodStart = new Date(start);
    const previousPeriodEnd = new Date(end);
    
    const periodDuration = end.getTime() - start.getTime();
    previousPeriodStart.setTime(start.getTime() - periodDuration);
    previousPeriodEnd.setTime(end.getTime() - periodDuration);
    
    const previousAffiliatesQuery = query(
      affiliatesRef,
      where('submittedAt', '>=', previousPeriodStart),
      where('submittedAt', '<=', previousPeriodEnd)
    );
    
    const previousCommissionsQuery = query(
      commissionsRef,
      where('createdAt', '>=', previousPeriodStart),
      where('createdAt', '<=', previousPeriodEnd)
    );
    
    const [
      previousAffiliatesSnapshot,
      previousCommissionsSnapshot
    ] = await Promise.all([
      getDocs(previousAffiliatesQuery),
      getDocs(previousCommissionsQuery)
    ]);
    
    let previousPeriodCommissions = 0;
    previousCommissionsSnapshot.forEach(doc => {
      const data = doc.data();
      previousPeriodCommissions += data.commissionAmount || 0;
    });
    
    const previous = {
      totalAffiliates: 0, // Historical data not tracked
      pendingApplications: 0, // Historical data not tracked
      approvedAffiliates: 0, // Historical data not tracked
      totalCommissionsPaid: previousPeriodCommissions, // Use previous period commissions for comparison
      newAffiliatesThisMonth: previousAffiliatesSnapshot.size,
      monthlyCommissions: previousPeriodCommissions
    };
    
    // Calculate percentage changes
    const calculatePercentageChange = (current: number, previous: number): number => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };
    
    const percentageChanges = {
      totalAffiliates: 0, // No historical comparison available
      pendingApplications: 0, // No historical comparison available
      approvedAffiliates: 0, // No historical comparison available
      totalCommissionsPaid: calculatePercentageChange(
        current.totalCommissionsPaid,
        previous.totalCommissionsPaid
      ),
      newAffiliatesThisMonth: calculatePercentageChange(
        current.newAffiliatesThisMonth,
        previous.newAffiliatesThisMonth
      ),
      monthlyCommissions: calculatePercentageChange(
        current.monthlyCommissions,
        previous.monthlyCommissions
      )
    };
    
    console.log('Affiliate statistics for period:', { current, previous, percentageChanges });
    
    return {
      current,
      previous,
      percentageChanges
    };
  } catch (error) {
    console.error(`Error getting affiliate statistics for period ${period}:`, error);
    throw new Error(`Failed to get affiliate statistics for period ${period}`);
  }
};

/**
 * Get commission source analytics using Firebase aggregation for optimal performance
 * Uses parallel aggregate queries to calculate totals and averages server-side
 * Benefits: Reduced bandwidth, faster queries, lower costs
 */
export const getCommissionSourceAnalytics = async (timePeriod: string = 'all_time') => {
  try {
    console.log('Getting commission source analytics with Firebase aggregation...');
    
    const commissionRef = collection(db, 'commissionRecords');
    
    // Build base query constraints with time period filter if specified
    let baseConstraints: any[] = [];
    if (timePeriod && timePeriod !== 'all_time') {
      const { start, end } = getDateRangeForPeriod(timePeriod as 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year');
      baseConstraints.push(where('createdAt', '>=', start));
      baseConstraints.push(where('createdAt', '<=', end));
    }

    // Run parallel aggregate queries for different sources
    const [linkAggregateSnapshot, discountAggregateSnapshot] = await Promise.all([
      // Link commissions (source = 'link' only, we'll handle missing sources separately)
      getAggregateFromServer(
        query(
          commissionRef,
          ...baseConstraints,
          where('source', '==', 'link')
        ),
        {
          count: count(),
          totalEarnings: sum('commissionAmount'),
          totalOrderValue: sum('orderValue'),
          avgCommission: average('commissionAmount'),
          avgOrderValue: average('orderValue')
        }
      ),
      
      // Discount code commissions
      getAggregateFromServer(
        query(
          commissionRef,
          ...baseConstraints,
          where('source', '==', 'discount_code')
        ),
        {
          count: count(),
          totalEarnings: sum('commissionAmount'),
          totalOrderValue: sum('orderValue'),
          avgCommission: average('commissionAmount'),
          avgOrderValue: average('orderValue')
        }
      )
    ]);

    // Extract aggregated data
    const linkData = linkAggregateSnapshot.data();
    const discountData = discountAggregateSnapshot.data();

    const linkStats = {
      count: linkData.count || 0,
      totalEarnings: linkData.totalEarnings || 0,
      totalOrderValue: linkData.totalOrderValue || 0,
      avgOrderValue: linkData.avgOrderValue || 0,
      avgCommission: linkData.avgCommission || 0
    };

    const discountStats = {
      count: discountData.count || 0,
      totalEarnings: discountData.totalEarnings || 0,
      totalOrderValue: discountData.totalOrderValue || 0,
      avgOrderValue: discountData.avgOrderValue || 0,
      avgCommission: discountData.avgCommission || 0
    };

    // Handle case where commissions without source exist - query for total and subtract explicit sources
    let commissionsWithoutSource = { count: 0, totalEarnings: 0, totalOrderValue: 0, avgCommission: 0, avgOrderValue: 0 };
    try {
      // Get total commissions in time period
      const totalSnapshot = await getAggregateFromServer(
        query(commissionRef, ...baseConstraints),
        {
          count: count(),
          totalEarnings: sum('commissionAmount'),
          totalOrderValue: sum('orderValue')
        }
      );

      const totalData = totalSnapshot.data();
      const totalCount = totalData.count || 0;
      const totalEarnings = totalData.totalEarnings || 0;
      const totalOrderValue = totalData.totalOrderValue || 0;
      
      // Calculate commissions without explicit source by subtracting explicit sources from total
      const explicitSourceCount = linkStats.count + discountStats.count;
      const explicitSourceEarnings = linkStats.totalEarnings + discountStats.totalEarnings;
      const explicitSourceOrderValue = linkStats.totalOrderValue + discountStats.totalOrderValue;
      
      const withoutSourceCount = Math.max(0, totalCount - explicitSourceCount);
      const withoutSourceEarnings = Math.max(0, totalEarnings - explicitSourceEarnings);
      const withoutSourceOrderValue = Math.max(0, totalOrderValue - explicitSourceOrderValue);
      
      commissionsWithoutSource = {
        count: withoutSourceCount,
        totalEarnings: withoutSourceEarnings,
        totalOrderValue: withoutSourceOrderValue,
        avgCommission: withoutSourceCount > 0 ? withoutSourceEarnings / withoutSourceCount : 0,
        avgOrderValue: withoutSourceCount > 0 ? withoutSourceOrderValue / withoutSourceCount : 0
      };
    } catch (error) {
      console.log('Error calculating commissions without source field:', error);
    }

    // Combine link stats with commissions that don't have a source (default to link)
    const combinedLinkStats = {
      count: linkStats.count + commissionsWithoutSource.count,
      totalEarnings: linkStats.totalEarnings + commissionsWithoutSource.totalEarnings,
      totalOrderValue: linkStats.totalOrderValue + commissionsWithoutSource.totalOrderValue,
      avgOrderValue: linkStats.count + commissionsWithoutSource.count > 0 
        ? (linkStats.totalOrderValue + commissionsWithoutSource.totalOrderValue) / (linkStats.count + commissionsWithoutSource.count) 
        : 0,
      avgCommission: linkStats.count + commissionsWithoutSource.count > 0 
        ? (linkStats.totalEarnings + commissionsWithoutSource.totalEarnings) / (linkStats.count + commissionsWithoutSource.count) 
        : 0
    };

    const totalCommissions = combinedLinkStats.count + discountStats.count;
    const totalEarnings = combinedLinkStats.totalEarnings + discountStats.totalEarnings;

    return {
      linkStats: combinedLinkStats,
      discountStats,
      summary: {
        totalCommissions,
        totalEarnings,
        linkPercentage: totalCommissions > 0 ? (combinedLinkStats.count / totalCommissions) * 100 : 0,
        discountPercentage: totalCommissions > 0 ? (discountStats.count / totalCommissions) * 100 : 0,
        linkEarningsPercentage: totalEarnings > 0 ? (combinedLinkStats.totalEarnings / totalEarnings) * 100 : 0,
        discountEarningsPercentage: totalEarnings > 0 ? (discountStats.totalEarnings / totalEarnings) * 100 : 0
      }
    };
  } catch (error) {
    console.error('Error getting commission source analytics:', error);
    throw error;
  }
};

/**
 * Get discount code performance analytics for affiliate commissions using hybrid aggregation
 */
export const getDiscountCodeAffiliatePerformance = async (timePeriod: string = 'all_time') => {
  try {
    console.log('Getting discount code affiliate performance with optimized queries...');
    
    const commissionRef = collection(db, 'commissionRecords');
    
    // Build base query constraints
    let baseConstraints: any[] = [where('source', '==', 'discount_code')];
    
    // Apply time period filter if specified
    if (timePeriod && timePeriod !== 'all_time') {
      const { start, end } = getDateRangeForPeriod(timePeriod as 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year');
      baseConstraints.push(where('createdAt', '>=', start));
      baseConstraints.push(where('createdAt', '<=', end));
    }

    // First, get unique discount code IDs with minimal data transfer
    const commissionsQuery = query(commissionRef, ...baseConstraints, orderBy('discountCodeId'));
    const commissionsSnapshot = await getDocs(commissionsQuery);
    
    // Extract unique discount code IDs and affiliate IDs for each
    const codePerformance: { [key: string]: any } = {};
    
    commissionsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const codeId = data.discountCodeId;
      const affiliateId = data.affiliateId;
      const commissionAmount = data.commissionAmount || 0;
      const orderValue = data.orderValue || 0;
      
      if (!codeId) return;

      if (!codePerformance[codeId]) {
        codePerformance[codeId] = {
          discountCodeId: codeId,
          commissionCount: 0,
          totalEarnings: 0,
          totalOrderValue: 0,
          affiliateIds: new Set()
        };
      }

      codePerformance[codeId].commissionCount++;
      codePerformance[codeId].totalEarnings += commissionAmount;
      codePerformance[codeId].totalOrderValue += orderValue;
      codePerformance[codeId].affiliateIds.add(affiliateId);
    });

    // Convert to array and calculate averages
    const performanceArray = Object.values(codePerformance).map((perf: any) => ({
      discountCodeId: perf.discountCodeId,
      commissionCount: perf.commissionCount,
      totalEarnings: perf.totalEarnings,
      totalOrderValue: perf.totalOrderValue,
      uniqueAffiliates: perf.affiliateIds.size,
      avgOrderValue: perf.commissionCount > 0 ? perf.totalOrderValue / perf.commissionCount : 0,
      avgCommission: perf.commissionCount > 0 ? perf.totalEarnings / perf.commissionCount : 0
    }));

    // Sort by total earnings descending
    performanceArray.sort((a, b) => b.totalEarnings - a.totalEarnings);

    console.log(`Processed ${performanceArray.length} unique discount codes with affiliate commissions`);
    return performanceArray;
  } catch (error) {
    console.error('Error getting discount code affiliate performance:', error);
    throw error;
  }
};

/**
 * Get affiliate statistics with month-over-month comparison
 */
export const getAffiliateStatisticsWithComparison = async () => {
  try {
    console.log('Getting affiliate statistics with comparison...');
    
    // Run all aggregate queries in parallel
    const [
      totalAffiliates,
      pendingApplications,
      approvedAffiliates,
      totalCommissionsPaid,
      currentMonthStats,
      previousMonthStats
    ] = await Promise.all([
      getTotalAffiliateCount(),
      getPendingApplicationCount(),
      getApprovedAffiliateCount(),
      getTotalCommissionsPaid(),
      getCurrentMonthAffiliateStats(),
      getPreviousMonthSamePeriodAffiliateStats()
    ]);

    const current = {
      totalAffiliates,
      pendingApplications,
      approvedAffiliates,
      totalCommissionsPaid,
      newAffiliatesThisMonth: currentMonthStats.newAffiliatesThisMonth,
      monthlyCommissions: currentMonthStats.monthlyCommissions
    };

    const previous = {
      totalAffiliates: 0, // Historical data not tracked
      pendingApplications: 0, // Historical data not tracked
      approvedAffiliates: 0, // Historical data not tracked
      totalCommissionsPaid: 0, // Historical data not tracked
      newAffiliatesThisMonth: previousMonthStats.newAffiliatesThisMonth,
      monthlyCommissions: previousMonthStats.monthlyCommissions
    };

    // Calculate percentage changes
    const calculatePercentageChange = (current: number, previous: number): number => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    const percentageChanges = {
      totalAffiliates: 0, // No historical comparison available
      pendingApplications: 0, // No historical comparison available
      approvedAffiliates: 0, // No historical comparison available
      totalCommissionsPaid: 0, // No historical comparison available
      newAffiliatesThisMonth: calculatePercentageChange(
        current.newAffiliatesThisMonth,
        previous.newAffiliatesThisMonth
      ),
      monthlyCommissions: calculatePercentageChange(
        current.monthlyCommissions,
        previous.monthlyCommissions
      )
    };

    console.log('Affiliate statistics with comparison:', { current, previous, percentageChanges });

    return {
      current,
      previous,
      percentageChanges
    };
  } catch (error) {
    console.error('Error getting affiliate statistics with comparison:', error);
    throw new Error('Failed to get affiliate statistics with comparison');
  }
};